import { Close, Tune } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as XSquare } from "assets/icons/xsquare.svg";
import { Box, Drawer, DrawerProps, Typography, useTheme } from "@mui/material";
import { useApproval, useTradeDrawer } from "hooks/useTradeDrawer";
import { buttonConfig, NumpadKey } from "components/TradeDrawer";
import { SmallNumberView } from "components/SmallValueView/SmallValueView";
import { getChainsByMode, Mode, MODES, TRADING_MODE_CONFIG_MAP } from "tradeConfigs";
import { useTradeContext } from "contexts/TradeContext";
import { Chain, CHAIN_CONFIG } from "chainConfigs";
import { Value } from "components/Value";
import { Col, Row } from "v2/components/base/Flex/ui";
import { RefetchPreview } from "components/CrossChainSwapDrawerV2/priceHint";
import { AmountTooSmallDrawer } from "components/CrossChainSwapDrawerV2/AmountTooSmallDrawer";
import { ConfirmDrawer } from "components/CrossChainSwapDrawerV2/ConfirmDrawer";
import { CrossChainTradeDrawerV2 as CrossChainTradeDrawerV1 } from "components/CrossChainSwapDrawerV2";
import { withVersions } from "v2/hocs/withVersions";
import { PreferencesDrawer } from "../PreferencesDrawer/ui";
import { styled } from "@mui/material";
import useUserStoreV2, { DEFAULT_CACHED_SLIPPAGE } from "store/user-store-v2/useUserStoreV2";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import BasicButton from "v2/components/Buttons/BasicButton";
import Select from "v2/components/base/Select/ui";
import Tabs from "v2/components/base/Tabs/ui";
import Skeleton from "../base/Skeleton/ui";
import RetroModal from "components/RetroModal";

interface CrossChainTradeDrawerV2Props {
  chain: Chain;
  groupId: number;
  isFromTrendingCall?: boolean;
  onProcessing?: () => void;
  onProcessed?: () => void;
  onConfirm?: (message?: string) => void;
  onFail?: (message?: string) => void;
  onClose: () => void;
  mode?: Mode;
}

export const CrossChainTradeDrawerV2 = (props: DrawerProps & CrossChainTradeDrawerV2Props) => {
  const {
    defaultMode,
    handleSetMode,
    mode: tradeMode,
    targetTokenPrice,
    targetTokenConfig,
    selectedNativeChain,
    selectedTargetChain,
    targetBalanceUsd,
    handleSetSelectedNativeChain,
    handleSetSelectedTargetChain,
    handleResetToDefaultTargetChain,
  } = useTradeContext();

  const {
    onClose,
    inputUsd,
    priceHint,
    onConfirm,
    targetAmount,
    nativeAmount,
    onNumpadPress,
    onPercentPress,
    isOpenConfirmDrawer,
    shouldConfirmDisabled,
    handleOpenConfirmDrawer,
    priceImpact,
    noValidRouteMessage,
    isLoadingPriceImpact,
    isRefetchingPriceImpact,
    refetchPriceImpact,
    isBalanceExceeded,
    setInputText,
    bridgeFee,
    inputText,
  } = useTradeDrawer({
    tgGroupId: props.groupId,
    isFromTrendingCall: props.isFromTrendingCall,
    onConfirm: props.onConfirm,
    onProcessing: props.onProcessing,
    onProcessed: props.onProcessed,
    onClose: () => {
      handleResetToDefaultTargetChain();
      setModeHandler(defaultMode);
      isCheckingAmountRef.current = false;
      props.onClose();
    },
    onFail: props.onFail,
    propsMode: props.mode,
  });

  const { isApproving, isApproveSucess, isGetCurrentAllowanceLoading, handleApproveAllowance } =
    useApproval({ targetAmount });

  const { getUserBalance } = useTradeStore();

  const mode = props.mode ?? tradeMode;
  const tradeModeConfig = TRADING_MODE_CONFIG_MAP[mode];

  // Slippage
  const { preferences = { slippage: DEFAULT_CACHED_SLIPPAGE } } = useUserStoreV2();
  const { slippage = 3 } = preferences;
  const [isOpenSlippageDrawer, setIsOpenSlippageDrawer] = useState(false);

  // price hint
  const [hintMessage, setHintMessage] = useState("");

  // Reminder Drawer
  const [shouldShowAmountTooSmall, setShouldShowAmountTooSmall] = useState(false);

  // selected token
  const [fromToken, setFromToken] = useState<string | null>(null);
  const [toToken, setToToken] = useState<string | null>(null);

  // check amount
  const isCheckingAmountRef = useRef(false);

  // Remindar Modal
  const [modalConfig, setModalConfig] = useState<{
    variant: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  const theme = useTheme();

  const isLoadingRef = useRef(false);

  // Slippage
  const handleOpenSlippageDrawer = () => {
    setIsOpenSlippageDrawer(true);
  };

  // Start Trading
  const handleConfirm = (slippage?: number) => {
    if (
      tradeModeConfig.minimumEntryUSD !== undefined &&
      +inputUsd < tradeModeConfig.minimumEntryUSD
    ) {
      setShouldShowAmountTooSmall(true);
      return;
    }

    handleOpenConfirmDrawer(true);
  };

  const onChangeSelect = (type: "from" | "to", chain: Chain) => {
    if (type === "to") {
      setToToken(chain);
      [MODES.BUY, MODES.SELL].includes(mode)
        ? handleSetSelectedNativeChain(chain)
        : handleSetSelectedTargetChain(chain);
    } else {
      handleSetSelectedNativeChain(chain);
      setFromToken(chain);
    }
  };

  const isTradingWrappedToken =
    [MODES.BUY, MODES.SELL].includes(mode) &&
    CHAIN_CONFIG[selectedTargetChain].wrappedTokenAddress ===
      targetTokenConfig[selectedTargetChain]?.token;

  const fromTokenList = useMemo(() => {
    if (tradeModeConfig.from.isNative) {
      return tradeModeConfig.supportedNativeChains[selectedNativeChain]!.filter(
        (chain) =>
          (!isTradingWrappedToken ||
            CHAIN_CONFIG[chain as Chain]?.wrappedTokenAddress !==
              targetTokenConfig[selectedTargetChain]?.token) &&
          (!tradeModeConfig.from.isBlockedSameChain || chain !== selectedNativeChain),
      ).map((chain) => {
        // const token = {chain,
        //   amount: getUserBalance(chain, "amount"),
        //   usdBalance: getUserBalance(chain, "usd"),
        //   imageUrl: CHAIN_CONFIG[chain as Chain].imageUrl,
        //   currency: CHAIN_CONFIG[chain as Chain].currency,
        // }
        return {
          label: (
            <>
              <img
                src={CHAIN_CONFIG[chain as Chain].imageUrl}
                alt={chain}
                width={16}
                height={16}
                style={{ borderRadius: "50%" }}
              />
              <Typography
                sx={{ width: "100%", textAlign: "center" }}
                variant="mobile-body-s"
                color={theme.palette.text.primary}>
                <Value
                  variant="mobile-body-s"
                  value={getUserBalance(chain, "usd") ?? 0}
                  useFixedDecimals
                  zeroCountSignificantDigits={2}
                  smallNumberSignificantDigits={2}
                />{" "}
                USD
              </Typography>
            </>
          ),
          value: chain,
        };
      });
    } else if (isTradingWrappedToken) {
      return [CHAIN_CONFIG[selectedTargetChain]].map((config) => ({
        label: (
          <>
            <img
              src={config.imageUrl}
              alt={config.name}
              width={16}
              height={16}
              style={{ borderRadius: "50%" }}
            />
            <Typography
              sx={{ width: "100%", textAlign: "center" }}
              variant="mobile-body-s"
              color={theme.palette.text.primary}>
              <Value
                variant="mobile-body-s"
                value={getUserBalance(config.name, "usd") ?? 0}
                useFixedDecimals
                zeroCountSignificantDigits={2}
                smallNumberSignificantDigits={2}
              />{" "}
              USD
            </Typography>
          </>
        ),
        value: config.name,
      }));
    } else if (targetTokenConfig[selectedTargetChain]) {
      return [targetTokenConfig[selectedTargetChain]].map((config) => ({
        label: (
          <>
            <img
              src={config.imageUrl}
              alt={selectedTargetChain}
              width={16}
              height={16}
              style={{ borderRadius: "50%" }}
            />
            <Typography
              sx={{ width: "100%", textAlign: "center" }}
              variant="mobile-body-s"
              color={theme.palette.text.primary}>
              <Value
                variant="mobile-body-s"
                value={targetBalanceUsd ?? 0}
                useFixedDecimals
                zeroCountSignificantDigits={2}
                smallNumberSignificantDigits={2}
              />{" "}
              USD
            </Typography>
          </>
        ),
        value: selectedTargetChain,
      }));
    }
  }, [
    tradeModeConfig.from.isNative,
    tradeModeConfig.from.isBlockedSameChain,
    tradeModeConfig.supportedNativeChains,
    isTradingWrappedToken,
    targetTokenConfig,
    selectedTargetChain,
    selectedNativeChain,
    theme.palette.text.primary,
    getUserBalance,
    targetBalanceUsd,
  ]);

  const toTokenList = useMemo(() => {
    if (tradeModeConfig.to.isNative) {
      return tradeModeConfig.supportedNativeChains[
        tradeModeConfig.to.isBlockedSameChain ? props.chain : selectedNativeChain
      ]!.filter((chain) => {
        return (
          (!tradeModeConfig.to.isBlockedSameChain || chain !== selectedNativeChain) &&
          (!isTradingWrappedToken ||
            CHAIN_CONFIG[chain as Chain]?.wrappedTokenAddress !==
              targetTokenConfig[selectedTargetChain]?.token)
        );
      }).map((chain) => {
        return {
          label: (
            <>
              <img src={CHAIN_CONFIG[chain as Chain].imageUrl} alt={chain} width={16} height={16} />
              <Typography
                sx={{ width: "100%", textAlign: "center" }}
                variant="mobile-body-s"
                color={theme.palette.text.primary}>
                {CHAIN_CONFIG[chain as Chain].currency}
              </Typography>
            </>
          ),
          value: chain,
        };
      });
    }
    return [targetTokenConfig[selectedTargetChain]].map((config) => ({
      label: (
        <>
          <img src={config.imageUrl} alt={selectedTargetChain} width={16} height={16} />
          <Typography
            sx={{ width: "100%", textAlign: "center" }}
            variant="mobile-body-s"
            color={theme.palette.text.primary}>
            {config.symbol}
          </Typography>
        </>
      ),
      value: selectedTargetChain,
    }));
  }, [
    tradeModeConfig.to.isNative,
    tradeModeConfig.to.isBlockedSameChain,
    tradeModeConfig.supportedNativeChains,
    targetTokenConfig,
    selectedTargetChain,
    props.chain,
    selectedNativeChain,
    isTradingWrappedToken,
    theme.palette.text.primary,
  ]);

  const setModeHandler = (mode: Mode) => {
    handleSetMode(mode);
    setFromToken(null);
    setToToken(null);
    isLoadingRef.current = false;
  };

  useEffect(() => {
    if (!isLoadingRef.current && !fromToken && !toToken && fromTokenList && toTokenList) {
      isLoadingRef.current = true;
      const fromTokenChain = props.chain ?? selectedNativeChain;

      setFromToken(isTradingWrappedToken ? fromTokenList[0]?.value : fromTokenChain);
      setToToken(isTradingWrappedToken ? toTokenList[0]?.value : selectedTargetChain);

      handleSetSelectedNativeChain(
        isTradingWrappedToken
          ? mode === "BUY"
            ? fromTokenList[0]?.value
            : toTokenList[0]?.value
          : fromTokenChain,
      );
    }
  }, [
    mode,
    toToken,
    fromToken,
    toTokenList,
    fromTokenList,
    handleSetSelectedNativeChain,
    isTradingWrappedToken,
    selectedNativeChain,
    selectedTargetChain,
    props.chain,
  ]);

  useEffect(() => {
    if ((!priceImpact && !noValidRouteMessage) || isLoadingPriceImpact || !!priceHint) {
      return setHintMessage("");
    }

    let hint = "Insufficient balance for trading";
    if (noValidRouteMessage) {
      hint = noValidRouteMessage;
      if (
        priceHint &&
        CHAIN_CONFIG[selectedTargetChain].isEvm &&
        !tradeModeConfig.from.isNative &&
        tradeModeConfig.to.isNative &&
        !isApproveSucess
      ) {
        hint = "Approval amount exceeded balance.";
      }

      setInputText("$0.00");
      setHintMessage(hint);
    } else if (isBalanceExceeded) {
      setInputText("$0.00");
      setHintMessage(hint);
    } else if (priceImpact && priceImpact / +inputUsd > 0.05) {
      setHintMessage("Price impact exceeded 5% of your trade size. Proceed with caution.");
    } else {
      setHintMessage("");
      setInputText(undefined);
    }
  }, [
    inputUsd,
    isApproveSucess,
    isBalanceExceeded,
    isLoadingPriceImpact,
    noValidRouteMessage,
    priceHint,
    priceImpact,
    selectedTargetChain,
    setInputText,
    targetAmount,
    toToken,
    tradeModeConfig.from.isNative,
    tradeModeConfig.to.isNative,
  ]);

  const onClickNumber = (index: number) => {
    isCheckingAmountRef.current = true;
    onNumpadPress(`${index + 1}` as NumpadKey);
  };

  const onClickPercent = (index: number) => {
    isCheckingAmountRef.current = false;
    onPercentPress(index);
  };

  return (
    <>
      <Drawer
        anchor={"bottom"}
        open={props.open}
        sx={{
          "& .MuiDrawer-paper": {
            width: "calc(100%)",
            borderRadius: "16px 16px 0 0",
            padding: "20px 24px",
            maxWidth: "1200px",
            marginX: "auto",
            backgroundColor: theme.palette.surface.bg,
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
          },
        }}>
        {/* Tool Bar */}
        <Row type="bc" sx={{ width: "100%", maxWidth: "436px", gap: "8px" }}>
          {/* Slippage Button */}
          <BasicButton
            variant="white"
            sx={{ borderRadius: "20px", aspectRatio: "1/1", height: "45px" }}
            onClick={handleOpenSlippageDrawer}>
            <Tune />
          </BasicButton>
          {/* Buy / Sell */}
          {TRADING_MODE_CONFIG_MAP[mode]?.modeSwitch ? (
            <>
              <Tabs.Group
                sx={{ aspectRatio: "4.5/1", height: "50px" }}
                tabColor={theme.palette.brand.primary}
                tabs={TRADING_MODE_CONFIG_MAP[mode]?.modeSwitch}
                selectedTab={TRADING_MODE_CONFIG_MAP[mode]?.modeSwitch?.indexOf(mode)}
                handleTabChange={(tab: number) =>
                  setModeHandler(TRADING_MODE_CONFIG_MAP[mode]?.modeSwitch?.[tab] as Mode)
                }
              />
            </>
          ) : null}

          {/* Close Button */}
          <BasicButton
            variant="white"
            sx={{ borderRadius: "20px", aspectRatio: "1/1", height: "45px" }}
            onClick={() => {
              isCheckingAmountRef.current = false;
              onClose();
            }}>
            <Close />
          </BasicButton>
        </Row>
        {/* Trade detail */}
        <Row type="cc" sx={{ maxWidth: "436px", marginTop: "32px", gap: "24px" }} fullWidth>
          {/* Source Token Select (balance USD) */}
          <Row type="cc" sx={{ flex: 1 }} fullWidth>
            <Select
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                border: `1px solid ${theme.palette.border.white}`,
                borderRadius: "16px",
                padding: "10px",
                width: "158px",
              }}
              defaultValue={fromToken}
              value={fromToken}
              onChange={(event: any) => onChangeSelect("from", event.target.value as Chain)}
              options={fromTokenList}
              paperSx={{ padding: "10px" }}
            />
          </Row>
          <Row sx={{ flex: 1, marginX: "-12px" }}>
            <Typography
              sx={{ marginX: "auto" }}
              variant="mobile-body-s-heavy"
              color="text.secondary">
              For
            </Typography>
          </Row>
          <Row type="cc" sx={{ flex: 1 }} fullWidth>
            <Select
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                border: `1px solid ${theme.palette.border.white}`,
                borderRadius: "16px",
                padding: "10px",
                width: "158px",
              }}
              defaultValue={toToken}
              value={toToken}
              onChange={(event: any) => onChangeSelect("to", event.target.value as Chain)}
              options={toTokenList}
              paperSx={{ padding: "10px" }}
            />
          </Row>
        </Row>

        {/* Price Display */}
        <Col
          type="cc"
          sx={{
            marginTop: theme.spacing(1),
            marginX: "auto",
            textAlign: "center",
            padding: "10px",
            gap: "4px",
          }}>
          {/* Entered Value (USD) */}
          <Skeleton.Dot
            isLoading={inputText === "LOADING..."}
            sx={{
              marginX: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Value
              variant="mobile-h1"
              color="brand.primary"
              value={inputUsd}
              isPrice
              useFixedDecimals
              useKMBTFormat={false}
              inputText={inputText}
            />
          </Skeleton.Dot>
          {/* Target Token (Amount) Received Preview */}
          <Row type="cc">
            <Typography variant="mobile-body-m" color={theme.palette.text.secondary}>
              <Row type="cc" sx={{ gap: "8px" }}>
                ≈{" "}
                <Value
                  variant="mobile-body-m"
                  value={tradeModeConfig.to.isNative ? nativeAmount : targetAmount}
                />
                {tradeModeConfig.to.isNative ? (
                  <img
                    src={CHAIN_CONFIG[selectedNativeChain as Chain].imageUrl}
                    alt={CHAIN_CONFIG[selectedNativeChain]?.symbol}
                    width={16}
                    height={16}
                    style={{ margin: "auto" }}
                  />
                ) : (
                  <img
                    src={targetTokenConfig[selectedTargetChain]?.imageUrl}
                    alt={targetTokenConfig[selectedTargetChain]?.symbol}
                    width={16}
                    height={16}
                    style={{ borderRadius: "50%" }}
                  />
                )}{" "}
                {/* ({numberFormatter.format(price)} USD) */}
              </Row>
            </Typography>
          </Row>
        </Col>
        {/* Remindar */}
        {!!hintMessage && (
          <RefetchPreview
            isLoading={isLoadingPriceImpact}
            isRefetching={isRefetchingPriceImpact}
            refetch={refetchPriceImpact}
            message={hintMessage}
          />
        )}
        {!!priceHint && !hintMessage && priceHint}
        {/* Percentage */}
        <Box
          display={"flex"}
          gap={theme.spacing(1)}
          marginTop={theme.spacing(1)}
          width={"fit-content"}
          marginX={"auto"}>
          {buttonConfig.map((item) => (
            <BasicButton
              variant="disabled"
              key={item.label}
              sx={{ padding: "14.5px 24px", backgroundColor: "#313339" }}
              onClick={() => onClickPercent(item.value)}>
              <Typography
                variant="mobile-body-s-heavy"
                color={theme.palette.text.primary}
                sx={{ textTransform: "capitalize" }}>
                {item.label.toLowerCase()}
              </Typography>
            </BasicButton>
          ))}
        </Box>

        {/* Numpad */}
        <Box
          marginTop={theme.spacing(1)}
          display={"flex"}
          flexWrap={"wrap"}
          width={"100%"}
          marginX={"auto"}
          maxWidth={"600px"}>
          {Array.from({ length: 9 }).map((_, index) => (
            <NumpadButton key={index} onClick={() => onClickNumber(index)}>
              <Typography variant="mobile-h2">{index + 1}</Typography>
            </NumpadButton>
          ))}
          <NumpadButton onClick={() => onNumpadPress(".")}>
            <Typography variant="mobile-h2">.</Typography>
          </NumpadButton>
          <NumpadButton onClick={() => onNumpadPress("0")}>
            <Typography variant="mobile-h2">0</Typography>
          </NumpadButton>
          <NumpadButton
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={() => onNumpadPress("delete")}>
            <XSquare />
          </NumpadButton>
        </Box>

        {/* Confirm Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            marginBottom: "4px",
          }}>
          <Typography variant="mobile-action-s-heavy" color={theme.palette.text.secondary}>
            {priceImpact && (
              <>
                <SmallNumberView value={priceImpact} variants="pre-title" isUSD />
                {" price impact  • "}
              </>
            )}
            {bridgeFee > 0 && (
              <>
                {"$"}
                <Value variant="mobile-action-s-heavy" value={bridgeFee} />
                {" bridge fee  • "}
              </>
            )}
            {slippage ? <Value variant="mobile-action-s-heavy" value={slippage} /> : "--"}% slippage
          </Typography>
        </Box>

        <Row type="cc" sx={{ maxHeight: "69px", width: "100%" }}>
          {isLoadingPriceImpact ||
          (CHAIN_CONFIG[selectedTargetChain].isEvm &&
            !tradeModeConfig.from.isNative &&
            tradeModeConfig.to.isNative &&
            !isApproveSucess) ? (
            <BasicButton
              sx={{ padding: "15px", width: "100%" }}
              variant={
                shouldConfirmDisabled || !!noValidRouteMessage || !!isBalanceExceeded
                  ? "disabled"
                  : "primary"
              }
              onClick={handleApproveAllowance}
              disabled={
                shouldConfirmDisabled ||
                isApproving ||
                isGetCurrentAllowanceLoading ||
                isLoadingPriceImpact
              }>
              <Typography variant="mobile-action-s-heavy">
                {isGetCurrentAllowanceLoading || isLoadingPriceImpact
                  ? "Loading..."
                  : "Approve Spending"}
              </Typography>
            </BasicButton>
          ) : (
            <BasicButton
              sx={{ padding: "15px", width: "100%" }}
              variant={
                !inputUsd || shouldConfirmDisabled || !!noValidRouteMessage || !!isBalanceExceeded
                  ? "disabled"
                  : tradeModeConfig.ctaColor
              }
              onClick={() => handleConfirm(slippage)}
              disabled={
                !inputUsd || shouldConfirmDisabled || !!noValidRouteMessage || !!isBalanceExceeded
              }>
              <Typography
                variant="mobile-action-s-heavy"
                sx={{ textTransform: "capitalize" }}
                color={
                  !inputUsd || shouldConfirmDisabled || !!noValidRouteMessage || !!isBalanceExceeded
                    ? theme.palette.text.secondary
                    : ["BUY", "SELL"].includes(mode)
                    ? theme.palette.text.primary
                    : theme.palette.text.brand
                }>
                Confirm {mode.toLowerCase()}
              </Typography>
            </BasicButton>
          )}
        </Row>
      </Drawer>

      {/* Amount too small drawer */}
      <AmountTooSmallDrawer
        isOpen={shouldShowAmountTooSmall}
        close={() => setShouldShowAmountTooSmall(false)}
      />

      {/* Confirm drawer */}
      <ConfirmDrawer
        mode={mode}
        isOpen={isOpenConfirmDrawer}
        close={() => handleOpenConfirmDrawer(false)}
        inputUsd={inputUsd}
        targetAmount={targetAmount}
        targetTokenPrice={targetTokenPrice ?? 0}
        targetTokenSymbol={targetTokenConfig[selectedTargetChain]?.symbol ?? ""}
        onConfirm={(slippage) => {
          onConfirm(slippage);
          handleOpenConfirmDrawer(false);
        }}
      />

      {/* Slippage Drawer */}
      <PreferencesDrawer
        isOpen={isOpenSlippageDrawer}
        close={() => setIsOpenSlippageDrawer(false)}
        showPriorityFee={
          CHAIN_CONFIG[getChainsByMode(mode, selectedNativeChain, selectedTargetChain).from]
            .hasPriorityFee
        }
        showValidatorTip={
          CHAIN_CONFIG[getChainsByMode(mode, selectedNativeChain, selectedTargetChain).from]
            .hasValidatorTip
        }
      />

      {/* Modal */}
      <RetroModal
        open={modalConfig !== null}
        onClose={() => {
          setModalConfig(null);
        }}
        variant={modalConfig?.variant ?? "success"}
        title={modalConfig?.title ?? ""}
        message={modalConfig?.message ?? ""}
      />
    </>
  );
};

const NumpadButton = styled("button")(({ theme }) => ({
  all: "unset",
  width: "33%",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  textAlign: "center",

  background: "transparent",
  border: "none",
  cursor: "pointer",
}));

const CrossChainSwapDrawer = withVersions({
  v1: CrossChainTradeDrawerV1,
  v2: CrossChainTradeDrawerV2,
});

export default CrossChainSwapDrawer;
